import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Copy,
  Check,
  Send,
  Phone,
  MapPin,
  FileText
} from "lucide-react";
import {
  FaGithub as Github,
  FaLinkedin as Linkedin,
  FaInstagram as Instagram
} from "react-icons/fa";
import { Bio } from "../../data/constanit";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 80px 24px 100px;

  @media (max-width: 768px) {
    padding: 60px 16px 80px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 860px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin-bottom: 36px;

  @media (max-width: 768px) {
    font-size: 14.5px;
    margin-bottom: 24px;
  }
`;

const ContactCard = styled(motion.div)`
  width: 100%;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  padding: 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);

  @media (max-width: 640px) {
    padding: 24px 18px;
    border-radius: 18px;
  }
`;

const InfoPillsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};

  span.pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 16px;
    border-radius: 999px;
    background: ${({ theme }) => theme.bgSoft};
    border: 1px solid ${({ theme }) => theme.border};
  }

  a.pill-link {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

const EmailBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  padding: 10px 18px;
  border-radius: 14px;
  max-width: 100%;
  flex-wrap: wrap;
  justify-content: center;
`;

const EmailAddress = styled.span`
  font-family: monospace;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
  word-break: break-all;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ copied, theme }) => (copied ? "#10b981" : `${theme.primary}22`)};
  color: ${({ copied, theme }) => (copied ? "#ffffff" : theme.primary)};
  border: 1px solid ${({ copied, theme }) => (copied ? "#10b981" : `${theme.primary}44`)};
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ copied, theme }) => (copied ? "#10b981" : `${theme.primary}33`)};
  }
`;

const ComposerForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  width: 100%;
`;

const PrimaryCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 26px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, #0284c7 100%);
  color: #030712;
  font-size: 14.5px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 18px ${({ theme }) => `${theme.primary}40`};
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px ${({ theme }) => `${theme.primary}60`};
  }
`;

const SecondaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
  }
`;

const SocialButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.border};
  width: 100%;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    transform: translateY(-2px);
  }
`;

const Contact = () => {
  const [copied, setCopied] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(Bio.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    const mailtoUrl = `mailto:${Bio.email}?subject=${encodeURIComponent(
      subject || "Inquiry for Vivek Carpenter"
    )}&body=${encodeURIComponent(message)}`;
    window.location.href = mailtoUrl;
  };

  return (
    <Section id="contact">
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Get In <span>Touch</span>
        </Title>

        <Description
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Open to full-time MERN stack roles, frontend development contracts, and collaborative software projects.
        </Description>

        <ContactCard
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <InfoPillsRow>
            <span className="pill">
              <MapPin size={14} color="#38bdf8" />
              <span>{Bio.location}</span>
            </span>
            <span className="pill">
              <Phone size={14} color="#10b981" />
              <a href={`tel:${Bio.phone}`} className="pill-link">
                {Bio.phone}
              </a>
            </span>
          </InfoPillsRow>

          <EmailBox>
            <EmailAddress>{Bio.email}</EmailAddress>
            <CopyButton
              copied={copied}
              onClick={handleCopyEmail}
              type="button"
            >
              {copied ? (
                <>
                  <Check size={14} /> Copied!
                </>
              ) : (
                <>
                  <Copy size={14} /> Copy Email
                </>
              )}
            </CopyButton>
          </EmailBox>

          <ComposerForm onSubmit={handleSendMessage}>
            <FormInput
              type="text"
              placeholder="Subject (e.g., MERN Developer Opportunity)"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
            <FormTextarea
              placeholder="Your message or project requirements..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              required
            />
            <ActionRow>
              <PrimaryCta type="submit">
                <Send size={15} /> Compose Direct Email
              </PrimaryCta>
              <SecondaryCta
                href={Bio.resume}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText size={15} /> Download Resume
              </SecondaryCta>
            </ActionRow>
          </ComposerForm>

          <SocialButtons>
            <SocialLink
              href={Bio.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Github size={15} /> GitHub
            </SocialLink>
            <SocialLink
              href={Bio.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Linkedin size={15} /> LinkedIn
            </SocialLink>
            <SocialLink
              href={Bio.insta}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram size={15} /> Instagram
            </SocialLink>
          </SocialButtons>
        </ContactCard>
      </ContentWrapper>
    </Section>
  );
};

export default Contact;
